import { dateDisplayToIso, isoToDateDisplay } from '@fluxora/ui';
import { z } from 'zod';

import {
  MAXIMUM_PLAUSIBLE_AGE_YEARS,
  adultDobCutoffIso,
  ageInYears,
} from '../utils/dob-format';

/**
 * The date-of-birth ELIGIBILITY RULE for the credit step (E-SF05).
 *
 * ── WHY THIS IS A SCHEMA AND NOT JUST A CALENDAR `max` ──────────────────────
 * `CreditPage` bounds the picker at `adultDobCutoffIso(new Date())`, which greys out
 * every day younger than 18. That is a CONVENIENCE. The field is a masked TEXT input:
 * a customer can type `01-01-2015`, paste it, or have a password manager fill it, and
 * none of those go anywhere near a disabled calendar cell. So the rule is enforced
 * here, on the value, and the page's `max` is derived from the same function so the
 * two can never drift apart.
 *
 * ⚠️ AND THIS IS STILL NOT THE CONTROL. `@rheora/enrollment-contract`'s
 * `CreditIdentitySchema.dateOfBirth` is a bare `/^\d{4}-\d{2}-\d{2}$/u` — its own
 * comment says "bounded to a plausible adult DOB range is enforced FE-side" — and
 * `US-Enrollment-BFF`'s `routes/credit.routes.ts` forwards the DOB to Accounts
 * untouched (verified 2026-08-04: no age check anywhere in `US-Enrollment-BFF/src`).
 * So a caller that never runs our JavaScript is NOT stopped by this. Adding the same
 * bound to the contract schema is a cross-repo change; it is proposed, not made here.
 * Never present this file as the reason an under-18 enrollment cannot happen.
 *
 * ── ERROR MESSAGES ARE i18n KEYS ───────────────────────────────────────────
 * Every message below is a `shop.` key, not English — `credit.service` lifts the
 * first issue's message straight into `IdentityFormErrors`, and the page resolves it
 * with `t()`. That is the same convention `validateSsn` follows.
 */

/**
 * A DOB the storefront will accept, as `YYYY-MM-DD`.
 *
 * `asOf` is injected rather than read from the clock inside, for the same reason
 * `validateIdentityForm` takes it: a rule that silently consults `new Date()` cannot
 * be tested at its own boundary, and the boundary is the entire point of an age rule.
 *
 * The four checks run in order and SHORT-CIRCUIT, so the customer is told the one
 * thing that is actually wrong:
 *   1. shape      — `YYYY-MM-DD` (the picker emits `''` for a partial value)
 *   2. real date  — 2026-02-30 satisfies the shape and is not a day
 *   3. 18+        — inclusive: an 18th birthday TODAY passes, tomorrow's does not
 *   4. plausible  — a mistyped year (1090) is a typo, not a supercentenarian
 */
export function adultDateOfBirthSchema(asOf: Date): z.ZodType<string> {
  const cutoff = adultDobCutoffIso(asOf);
  return z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'shop.credit.form.dob.invalid')
    .refine(isRealCalendarDate, 'shop.credit.form.dob.invalid')
    // ISO strings compare chronologically, so "at least 18" is one string comparison
    // against the same cutoff the calendar's `max` uses. Inclusive by design.
    .refine((iso) => iso <= cutoff, 'shop.credit.form.dob.adult')
    .refine(
      (iso) => ageInYears(iso, asOf) <= MAXIMUM_PLAUSIBLE_AGE_YEARS,
      'shop.credit.form.dob.implausible',
    );
}

/**
 * Does this ISO date exist? `2026-02-30` and `1990-13-01` both pass a `\d{4}-\d{2}-\d{2}`
 * regex, and a `Date` will happily roll them into March and January-next-year.
 *
 * Delegated to `@fluxora/ui`'s `dateDisplayToIso`/`isoToDateDisplay` round trip — the
 * SAME pair the DatePicker uses to decide what to emit — rather than a second
 * hand-rolled rollover check here. One arbiter for "is this a day".
 */
function isRealCalendarDate(iso: string): boolean {
  const display = isoToDateDisplay(iso, 'mm-dd-yyyy');
  return display !== '' && dateDisplayToIso(display, 'mm-dd-yyyy') === iso;
}

/**
 * The first i18n error key for `dobIso`, or `undefined` when it is acceptable.
 *
 * A thin read of `safeParse` so callers do not each reach into `error.issues`, and so
 * "which message wins" is decided once, here, by the refine order above.
 */
export function dateOfBirthErrorKey(dobIso: string, asOf: Date): string | undefined {
  const parsed = adultDateOfBirthSchema(asOf).safeParse(dobIso);
  if (parsed.success) return undefined;
  return parsed.error.issues[0]?.message ?? 'shop.credit.form.dob.invalid';
}
